import { useEffect } from "react";
import axios from "axios";
import { Lock } from "lucide-react";
import Navigation from "@/components/Navigation";
import { useAuth } from "@/context/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const TIER_LABELS = {
  public: "WHIP Public",
  pro: "WHIP Pro",
  intelligence: "WHIP Intelligence",
  enterprise: "WHIP Enterprise",
  admin: "WHIP Admin",
  tools: "Tools add-on",
  network: "Network & Advanced add-on",
};

// Mirrors backend entitlements.FEATURE_GROUPS -> lowest tier / add-on that unlocks a feature.
const FORECASTING = ["whip_probability", "whip_probability_full", "vote_prediction_member_level", "forecast_history", "live_signals", "just_introduced", "stalled_forgotten"];
const LEGISLATOR_INTEL = ["voting_history", "legislator_profile_advanced", "legislative_effectiveness", "ideology_analysis", "wealth_analysis", "surprise_votes",
  "strategic_read", "legislator_brief", "campaign_finance", "leadership_read", "cosponsorship_activity", "position_shifts"];
const TOOLS = ["legislator_rankings", "legislation_rankings", "compare", "watchlists", "alerts", "fit_scorecards"];
const NETWORK = ["alliance_map", "power_brokers", "bridge_builders", "ai_radar", "exports", "team_workspace"];
export const REQUIRED_FOR = Object.fromEntries([
  ...FORECASTING.map((f) => [f, "pro"]), ...LEGISLATOR_INTEL.map((f) => [f, "intelligence"]),
  ...TOOLS.map((f) => [f, "tools"]), ...NETWORK.map((f) => [f, "network"]),
]);
const requiredFor = (feature, fallback) => REQUIRED_FOR[feature] || fallback;

const TIER_PILL_DARK = {
  public: "text-white/60 border-white/20 bg-white/5",
  pro: "text-sky-300 border-sky-500/40 bg-sky-500/10",
  intelligence: "text-orange-300 border-orange-500/40 bg-orange-500/10",
  enterprise: "text-amber-300 border-amber-500/40 bg-amber-500/10",
  admin: "text-rose-300 border-rose-500/40 bg-rose-500/10",
  tools: "text-emerald-300 border-emerald-500/40 bg-emerald-500/10",
  network: "text-purple-300 border-purple-500/40 bg-purple-500/10",
};

const TIER_PILL_LIGHT = {
  public: "text-gray-600 border-gray-300 bg-gray-50",
  pro: "text-sky-700 border-sky-300 bg-sky-50",
  intelligence: "text-orange-700 border-orange-300 bg-orange-50",
  enterprise: "text-amber-700 border-amber-300 bg-amber-50",
  admin: "text-rose-700 border-rose-300 bg-rose-50",
  tools: "text-emerald-700 border-emerald-300 bg-emerald-50",
  network: "text-purple-700 border-purple-300 bg-purple-50",
};

export const TierBadge = ({ tier = "pro", light = false, className = "" }) => (
  <span
    className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] font-mono font-bold uppercase tracking-wider ${
      (light ? TIER_PILL_LIGHT : TIER_PILL_DARK)[tier] || TIER_PILL_DARK.pro
    } ${className}`}
    data-testid={`tier-badge-${tier}`}
  >
    {TIER_LABELS[tier] || tier}
  </span>
);

/** Inline locked row, for lists inside an otherwise-visible panel. */
export const LockedRow = ({ label, tier = "pro" }) => (
  <div
    className="flex items-center justify-between rounded-md border border-white/10 bg-white/[0.02] px-3 py-2"
    data-testid={`locked-row-${label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
  >
    <span className="text-sm text-white/55">{label}</span>
    <span className="flex items-center gap-1.5 text-[10px] font-mono font-bold uppercase tracking-wider text-white/40">
      <Lock className="w-3 h-3" /> {tier}
    </span>
  </div>
);

/** In-place locked card for a premium panel. */
export const LockedPanel = ({ feature, tier: tierProp = "pro", title, blurb, light = false, className = "" }) => {
  const tier = requiredFor(feature, tierProp);
  useEffect(() => {
    axios.post(`${API}/analytics/track`, { feature_key: feature, event: "locked_view", context: title || "" }).catch(() => {});
  }, [feature, title]);
  return (
  <div
    className={
      light
        ? `rounded-xl border-2 border-dashed border-gray-200 bg-white p-6 ${className}`
        : `rounded-xl border border-dashed border-white/15 bg-white/[0.02] p-6 ${className}`
    }
    data-testid={`locked-${feature}`}
  >
    <div className="flex items-start gap-3.5">
      <div
        className={`shrink-0 rounded-full p-2.5 ${
          light ? "bg-orange-50 text-orange-500" : "bg-orange-500/10 text-orange-400"
        }`}
      >
        <Lock className="w-4 h-4" />
      </div>
      <div className="min-w-0">
        <div className="flex items-center gap-2.5 flex-wrap">
          <h3 className={`font-bold ${light ? "text-gray-900" : "text-white"}`}>{title}</h3>
          <TierBadge tier={tier} light={light} />
        </div>
        {blurb && (
          <p className={`text-sm mt-1.5 leading-relaxed ${light ? "text-gray-500" : "text-white/50"}`}>{blurb}</p>
        )}
        <p className={`text-[11px] mt-2.5 ${light ? "text-gray-400" : "text-white/35"}`}>
          Included in {TIER_LABELS[tier] || tier}, ask your workspace administrator about upgrading.
        </p>
      </div>
    </div>
  </div>
  );
};

/** Full-page locked state (keeps nav so the feature stays discoverable). */
export const PageLock = ({ feature, tier = "pro", title, blurb }) => (
  <div className="min-h-screen" style={{ background: "var(--whip-bg, #0b0b0f)" }}>
    <Navigation />
    <main className="max-w-2xl mx-auto px-4 sm:px-6 py-24" data-testid={`page-lock-${feature}`}>
      <LockedPanel feature={feature} tier={tier} title={title} blurb={blurb} />
    </main>
  </div>
);

/** Route wrapper, renders PageLock when the account lacks the feature. */
export const FeatureRoute = ({ feature, tier = "pro", title, blurb, children }) => {
  const { featureMode } = useAuth();
  if (featureMode(feature) === "none") {
    return <PageLock feature={feature} tier={tier} title={title} blurb={blurb} />;
  }
  return children;
};

/** Compact upgrade strip shown under a preview of premium content. */
export const UpgradeNudge = ({ feature, tier: tierProp = "pro", text, light = false, className = "" }) => {
  const tier = requiredFor(feature, tierProp);
  useEffect(() => {
    axios.post(`${API}/analytics/track`, { feature_key: feature, event: "preview_view", context: text || "" }).catch(() => {});
  }, [feature, text]);
  return (
    <div
      className={`flex items-center gap-2.5 rounded-lg border border-dashed px-3 py-2 text-xs ${
        light ? "border-gray-300 bg-gray-50 text-gray-600" : "border-white/15 bg-white/[0.03] text-white/60"
      } ${className}`}
      data-testid={`upgrade-nudge-${feature}`}
    >
      <Lock className={`w-3.5 h-3.5 shrink-0 ${light ? "text-orange-500" : "text-orange-400"}`} />
      <span className="flex-1">{text || "You are seeing a preview."}</span>
      <TierBadge tier={tier} light={light} />
    </div>
  );
};
